import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import RestorationManager from "../components/RestorationManager";
import PhotosManager from "../components/PhotosManager";

const empty = {
    name: "",
    manufacturer: "",
    year: 1980,
    country: "",
    cpu: "",
    ram: "",
    storage: "",
    original_price: "",
    current_value: "",
    condition: "Good",
    story: "",
    tags: "",
    image_url: "",
    images: [],
    acquisition_source: "",
    acquisition_date: "",
    price_paid: "",
    insurance_value: "",
    provenance_notes: "",
};

const CONDITIONS = ["Mint", "Excellent", "Good", "Fair", "Restoration"];

export default function AdminForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const editing = !!id;

    useEffect(() => {
        if (!id) return;
        (async () => {
            const { data } = await api.get(`/computers/${id}`);
            // If legacy record has image_url but empty images[], seed images
            const images = (data.images && data.images.length > 0) ? data.images : (data.image_url ? [data.image_url] : []);
            setForm({
                ...empty,
                ...data,
                images,
                tags: (data.tags || []).join(", "),
                original_price: data.original_price ?? "",
                current_value: data.current_value ?? "",
                price_paid: data.price_paid ?? "",
                insurance_value: data.insurance_value ?? "",
                acquisition_source: data.acquisition_source || "",
                acquisition_date: data.acquisition_date || "",
                provenance_notes: data.provenance_notes || "",
            });
        })();
    }, [id]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const images = form.images || [];
            const payload = {
                ...form,
                year: Number(form.year),
                original_price: form.original_price === "" ? null : Number(form.original_price),
                current_value: form.current_value === "" ? null : Number(form.current_value),
                price_paid: form.price_paid === "" ? null : Number(form.price_paid),
                insurance_value: form.insurance_value === "" ? null : Number(form.insurance_value),
                tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
                images,
                image_url: images[0] || form.image_url || null, // cover for legacy consumers
                acquisition_source: form.acquisition_source || null,
                acquisition_date: form.acquisition_date || null,
                provenance_notes: form.provenance_notes || "",
            };
            if (editing) {
                await api.put(`/computers/${id}`, payload);
                toast.success("Record updated.");
            } else {
                await api.post("/computers", payload);
                toast.success("Record added.");
            }
            navigate("/admin");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-form-page" className="mx-auto max-w-4xl px-6 lg:px-10 pt-10 pb-24">
            <div className="overline">// {editing ? "Edit" : "New"} Record</div>
            <h1 className="mt-2 font-serif text-5xl">
                {editing ? "Amend the record" : "New exhibit"}
            </h1>

            <form onSubmit={submit} className="mt-12 space-y-10" data-testid="computer-form">
                {/* Core specs */}
                <SectionHeader label="// Core Specs" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="Name" testid="form-name" value={form.name} onChange={set("name")} required />
                    <Field label="Manufacturer" testid="form-manufacturer" value={form.manufacturer} onChange={set("manufacturer")} required />
                    <Field label="Year" testid="form-year" type="number" value={form.year} onChange={set("year")} required />
                    <Field label="Country" testid="form-country" value={form.country} onChange={set("country")} />
                    <Field label="CPU" testid="form-cpu" value={form.cpu} onChange={set("cpu")} />
                    <Field label="RAM" testid="form-ram" value={form.ram} onChange={set("ram")} />
                    <Field label="Storage" testid="form-storage" value={form.storage} onChange={set("storage")} />
                    <div>
                        <Label>Condition</Label>
                        <select
                            data-testid="form-condition"
                            value={form.condition}
                            onChange={set("condition")}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber"
                        >
                            {CONDITIONS.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                    <Field label="Original price (USD)" testid="form-original-price" type="number" value={form.original_price} onChange={set("original_price")} />
                    <Field label="Current value (USD)" testid="form-current-value" type="number" value={form.current_value} onChange={set("current_value")} />
                </div>

                <div>
                    <Label>Story / Description</Label>
                    <textarea
                        data-testid="form-story"
                        value={form.story}
                        onChange={set("story")}
                        rows={5}
                        className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber leading-relaxed"
                        placeholder="The story that makes this machine matter…"
                    />
                </div>

                <Field label="Tags (comma-separated)" testid="form-tags" value={form.tags} onChange={set("tags")} placeholder="8-bit, home, iconic" />

                {/* Photos */}
                <div>
                    <SectionHeader label="// Photos" />
                    <PhotosManager
                        images={form.images}
                        onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))}
                    />
                </div>

                {/* Provenance */}
                <div>
                    <SectionHeader label="// Provenance &amp; Acquisition" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field label="Acquired from" testid="form-acq-source" value={form.acquisition_source} onChange={set("acquisition_source")} placeholder="eBay auction, estate sale…" />
                        <Field label="Acquisition date" testid="form-acq-date" type="date" value={form.acquisition_date} onChange={set("acquisition_date")} />
                        <Field label="Price paid (USD)" testid="form-price-paid" type="number" value={form.price_paid} onChange={set("price_paid")} />
                        <Field label="Insurance value (USD)" testid="form-insurance-value" type="number" value={form.insurance_value} onChange={set("insurance_value")} />
                    </div>
                    <div className="mt-6">
                        <Label>Provenance notes</Label>
                        <textarea
                            data-testid="form-provenance-notes"
                            value={form.provenance_notes}
                            onChange={set("provenance_notes")}
                            rows={4}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber leading-relaxed"
                            placeholder="Chain of custody, previous owners, provenance letters…"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-4 pt-4">
                    <button
                        type="submit"
                        data-testid="form-submit"
                        disabled={saving}
                        className="border border-phosphor-amber bg-phosphor-amber px-6 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-background hover:bg-transparent hover:text-phosphor-amber transition-colors disabled:opacity-50"
                    >
                        {saving ? "Saving…" : editing ? "Save changes ▸" : "Add to archive ▸"}
                    </button>
                    <button
                        type="button"
                        data-testid="form-cancel"
                        onClick={() => navigate("/admin")}
                        className="border border-border px-6 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground"
                    >
                        Cancel
                    </button>
                </div>
            </form>

            {editing && (
                <div className="mt-16">
                    <SectionHeader label="// Restoration History" />
                    <h2 className="font-serif text-3xl mb-6">Log of works &amp; care</h2>
                    <RestorationManager computerId={id} />
                </div>
            )}
        </div>
    );
}

function SectionHeader({ label }) {
    return <div className="overline mb-4 mt-2">{label}</div>;
}

function Label({ children }) {
    return <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{children}</div>;
}

function Field({ label, testid, ...rest }) {
    return (
        <div>
            <Label>{label}</Label>
            <input
                data-testid={testid}
                {...rest}
                className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber"
            />
        </div>
    );
}
